import { Button } from '@mui/material';
import { useRouter } from 'next/router';
import { type ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { replacePathParams } from 'utils/routes.utils';

import { Routes } from 'constants/routes.constants';
import { useTranslatedText } from 'hooks/useTranslatedText';
import type { Ticket } from 'restful/tickets';
import { hasPaidCatalogueAddons } from 'restful/tickets/addon.utils';
import { useTrackViewItem } from 'services/datalayer/ecomm/useTrackViewItem';
import { TicketStatusFragment } from 'types/api/dato-operations.types';

import LinkUnstyled from 'components/@common/LinkUnstyled';

import TicketItemRow from './TicketItemRow';

interface TicketInquiryActionProps {
  ticketId: string;
}

export const TicketInquiryAction = ({ ticketId }: TicketInquiryActionProps) => {
  const { query } = useRouter();
  const slug = query.slug?.toString() ?? '';

  return (
    <Button
      component={LinkUnstyled}
      href={replacePathParams(Routes.EventInquiry, {
        slug,
        id: ticketId,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      <FormattedMessage id="tickets.inquiry.default.request.label" />
    </Button>
  );
};

interface Props {
  ticket: Ticket;
  status?: TicketStatusFragment;
  hideInquiryButton?: boolean;
  details?: ReactNode;
  onOpenDetail?: (ticket: Ticket) => void;
}

const TicketInquiryItem = ({
  ticket,
  status,
  hideInquiryButton = false,
  details,
  onOpenDetail,
}: Props) => {
  const localize = useTranslatedText();
  const handleViewItem = useTrackViewItem();
  const [isExpanded, setIsExpanded] = useState(false);
  const description = localize(ticket.description);
  const hasDetails =
    !!details &&
    (!!description || !!ticket.perks?.length || hasPaidCatalogueAddons(ticket));

  return (
    <TicketItemRow
      ticket={ticket}
      status={status}
      details={hasDetails ? details : undefined}
      isExpanded={isExpanded}
      onOpenDetail={onOpenDetail}
      onToggleExpand={
        hasDetails
          ? () => {
              setIsExpanded((expanded) => {
                const nextExpanded = !expanded;
                if (nextExpanded) handleViewItem(ticket);
                return nextExpanded;
              });
            }
          : undefined
      }
    >
      {!hideInquiryButton && <TicketInquiryAction ticketId={ticket.id} />}
    </TicketItemRow>
  );
};

export default TicketInquiryItem;
